import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Paper,
  Avatar,
  Stack,
  Alert,
  Collapse,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CampaignIcon from '@mui/icons-material/Campaign';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from './CustomNotification';
import { getAllBranches } from '../data/branches';
import type { Branch } from '../data/employees';
import {
  publishMessage,
  getMessages,
  type BroadcastMessage,
  type PublishMessageParams,
} from '../data/broadcasts';
import { brand } from '../../shared-theme/themePrimitives';

export default function BroadcastMessages() {
  const { t, direction } = useLanguage();
  const { user } = useAuth();
  const { show: showNotification } = useNotification();
  const isRtl = direction === 'rtl';
  const isAdmin = user?.role === 'GM';

  // Form state
  const [message, setMessage] = React.useState('');
  const [branchType, setBranchType] = React.useState<'all' | 'specific'>('all');
  const [selectedBranchId, setSelectedBranchId] = React.useState<number | null>(null);
  const [branches, setBranches] = React.useState<Branch[]>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);

  // Messages state
  const [messages, setMessages] = React.useState<BroadcastMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [showPublishForm, setShowPublishForm] = React.useState(false);

  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);

  // Fetch branches for admin
  React.useEffect(() => {
    if (isAdmin) {
      const fetchBranches = async () => {
        setLoadingBranches(true);
        try {
          const branchesData = await getAllBranches();
          setBranches(branchesData);
        } catch (error) {
          console.error('Failed to fetch branches:', error);
        } finally {
          setLoadingBranches(false);
        }
      };
      fetchBranches();
    }
  }, [isAdmin]);

  // Fetch messages
  const fetchMessages = React.useCallback(async (page: number = 1, append: boolean = false) => {
    setLoadingMessages(true);
    try {
      const result = await getMessages({
        per_page: 20,
        page,
      });

      if (append) {
        setMessages((prev) => [...prev, ...result.data]);
      } else {
        setMessages(result.data);
      }

      setCurrentPage(result.current_page);
      setHasMore(result.current_page < result.last_page);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      showNotification(t('broadcasts.publishError'), { severity: 'error' });
    } finally {
      setLoadingMessages(false);
    }
  }, [showNotification, t]);

  React.useEffect(() => {
    fetchMessages(1, false);
  }, [fetchMessages]);

  // Handle scroll for infinite loading
  const handleScroll = React.useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || loadingMessages || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when scrolled to bottom (with 100px threshold)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchMessages(currentPage + 1, true);
    }
  }, [loadingMessages, hasMore, currentPage, fetchMessages]);

  React.useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handlePublish = async () => {
    if (!message.trim()) {
      showNotification(t('broadcasts.messageRequired'), { severity: 'error' });
      return;
    }

    if (branchType === 'specific' && !selectedBranchId) {
      showNotification(t('broadcasts.branchRequired'), { severity: 'error' });
      return;
    }

    setPublishing(true);
    try {
      const params: PublishMessageParams = {
        message: message.trim(),
        is_all_branches: branchType === 'all',
      };

      if (branchType === 'specific' && selectedBranchId) {
        params.branch_id = selectedBranchId;
      }

      await publishMessage(params);
      showNotification(t('broadcasts.publishSuccess'), { severity: 'success' });

      // Reset form
      setMessage('');
      setBranchType('all');
      setSelectedBranchId(null);

      // Refresh messages
      fetchMessages(1, false);
    } catch (error) {
      console.error('Failed to publish message:', error);
      showNotification(t('broadcasts.publishError'), { severity: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return t('broadcasts.justNow');
    if (diffInMinutes < 60) return t('broadcasts.minutesAgo', { count: diffInMinutes });
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return t('broadcasts.hoursAgo', { count: diffInHours });
    
    const diffInDays = Math.floor(diffInHours / 24);
    return t('broadcasts.daysAgo', { count: diffInDays });
  };

  return (
    <PageContainer
      breadcrumbs={[{ title: t('broadcasts.title') }]}
    >
      <Box sx={{ maxWidth: 1400, p: 3 }}>
        {/* Admin Publish Form */}
        {isAdmin && (
          <>
            <Box sx={{ mb: 3, display: 'flex' }}>
              <Tooltip title={showPublishForm ? t('broadcasts.hidePublishForm') : t('broadcasts.showPublishForm')}>
                <IconButton
                  onClick={() => setShowPublishForm(!showPublishForm)}
                  sx={{
                    width: 48,
                    height: 48,
                    background: `linear-gradient(135deg, ${brand[400]} 0%, ${brand[600]} 100%)`,
                    color: 'white',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[700]} 100%)`,
                    },
                  }}
                >
                  {showPublishForm ? <CloseIcon /> : <AddIcon />}
                </IconButton>
              </Tooltip>
            </Box>

            <Collapse in={showPublishForm}>
              <Card
                sx={{
                  mb: 4,
                  background: `linear-gradient(135deg, ${brand[50]} 0%, ${brand[100]} 100%)`,
                  border: `1px solid ${brand[200]}`,
                  boxShadow: '0 4px 20px rgba(2, 139, 203, 0.1)',
                }}
              >
                <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                <Avatar
                  sx={{
                    bgcolor: brand[700],
                    width: 48,
                    height: 48,
                    mr: '5px',
                  }}
                >
                  <CampaignIcon sx={{ color: 'white' }} />
                </Avatar>
                <Typography variant="h5" fontWeight={600} color={brand[700]}>
                  {t('broadcasts.publishMessage')}
                </Typography>
              </Stack>

              <TextField
                fullWidth
                multiline
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('broadcasts.writeMessage')}
                variant="outlined"
                sx={{
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                    height: '150px',
                    overflow: 'hidden',
                  },
                  '& .MuiInputBase-input': {
                    height: '150px !important',
                    overflow: 'auto !important',
                    alignItems: 'flex-start',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(0,0,0,0.1)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '4px',
                      '&:hover': {
                        background: 'rgba(0,0,0,0.4)',
                      },
                    },
                  }
                }}
                InputLabelProps={{
                  sx: {
                    textAlign: isRtl ? 'right' : 'left',
                    right: isRtl ? 0 : 'auto',
                    left: isRtl ? 'auto' : 0,
                    transformOrigin: isRtl ? 'top right' : 'top left'
                  }
                }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: isRtl ? 'right' : 'left',
                    marginRight: isRtl ? '14px' : 0,
                    marginLeft: isRtl ? 0 : '14px'
                  }
                }}
              />

              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <RadioGroup
                  row
                  value={branchType}
                  onChange={(e) => setBranchType(e.target.value as 'all' | 'specific')}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PublicIcon fontSize="small" sx={{ color: 'black' }} />
                        <span style={{ color: 'black' }}>{t('broadcasts.allBranches')}</span>
                      </Stack>
                    }
                  />
                  <FormControlLabel
                    value="specific"
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessIcon fontSize="small" sx={{ color: 'black' }} />
                        <span style={{ color: 'black' }}>{t('broadcasts.specificBranch')}</span>
                      </Stack>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {branchType === 'specific' && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel
                    sx={{
                      transformOrigin: isRtl ? 'top right' : 'top left',
                      right: isRtl ? 0 : 'auto',
                      left: isRtl ? 'auto' : 0,
                      color: 'black',
                    }}
                  >
                    {t('broadcasts.selectBranch')}
                  </InputLabel>
                  <Select
                    value={selectedBranchId || ''}
                    onChange={(e) => setSelectedBranchId(e.target.value as number)}
                    label={t('broadcasts.selectBranch')}
                    disabled={loadingBranches}
                    sx={{ backgroundColor: 'white' }}
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={handlePublish}
                  disabled={publishing || !message.trim()}
                  sx={{
                    width: 56,
                    height: 56,
                    background: `linear-gradient(135deg, ${brand[400]} 0%, ${brand[600]} 100%)`,
                    color: 'white',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[700]} 100%)`,
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    },
                  }}
                >
                  {publishing ? <CircularProgress size={24} sx={{ color: 'white' }} /> : <SendIcon />}
                </IconButton>
              </Box>
            </CardContent>
          </Card>
            </Collapse>
          </>
        )}

        {/* Messages History */}
        <Card sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={3} color={brand[700]}>
              {t('broadcasts.messageHistory')}
            </Typography>

            <Box
              ref={messagesContainerRef}
              sx={{
                maxHeight: 600,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {messages.length === 0 && !loadingMessages ? (
                <Alert severity="info" icon={<CampaignIcon />}>
                  {t('broadcasts.noMessages')}
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {messages.map((msg, index) => (
                    <Paper
                      key={msg.id}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: brand[300],
                          boxShadow: `0 2px 8px ${brand[100]}`,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2}>
                        <Box flex={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {msg.user?.name || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTimeAgo(msg.created_at)}
                            </Typography>
                          </Stack>

                          <Typography variant="body1" mb={1.5} sx={{ whiteSpace: 'pre-wrap' }}>
                            {msg.message}
                          </Typography>

                          <Stack direction="row" spacing={1} alignItems="center">
                            {msg.is_all_branches ? (
                              <>
                                <PublicIcon fontSize="small" color="primary" />
                                <Typography variant="caption" color="primary">
                                  {t('broadcasts.toAllBranches')}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <BusinessIcon fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                  {t('broadcasts.toBranch')}: {msg.branch?.name || 'N/A'}
                                </Typography>
                              </>
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}

                  {loadingMessages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={30} />
                    </Box>
                  )}
                </Stack>
              )}
              <div ref={messagesEndRef} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
}

